export type DiffOp = 'equal' | 'insert' | 'delete';

export interface DiffToken {
  op: DiffOp;
  value: string;
}

/**
 * Diff por palavra entre o valor atual e o sugerido.
 *
 * É por palavra e não por caractere porque o revisor está julgando texto de
 * catálogo: destacar letras isoladas dentro de uma frase reescrita gera ruído
 * em vez de informação. Implementado aqui em vez de trazer uma biblioteca — são
 * poucas linhas e o comportamento precisa ser exatamente este.
 */
export function diffWords(before: string, after: string): DiffToken[] {
  const left = tokenize(before);
  const right = tokenize(after);

  const table = buildLcsTable(left, right);
  const tokens: DiffToken[] = [];

  let i = 0;
  let j = 0;
  while (i < left.length && j < right.length) {
    if (left[i] === right[j]) {
      push(tokens, 'equal', left[i] ?? '');
      i += 1;
      j += 1;
    } else if ((table[i + 1]?.[j] ?? 0) >= (table[i]?.[j + 1] ?? 0)) {
      push(tokens, 'delete', left[i] ?? '');
      i += 1;
    } else {
      push(tokens, 'insert', right[j] ?? '');
      j += 1;
    }
  }
  while (i < left.length) {
    push(tokens, 'delete', left[i] ?? '');
    i += 1;
  }
  while (j < right.length) {
    push(tokens, 'insert', right[j] ?? '');
    j += 1;
  }

  return tokens;
}

function tokenize(value: string): string[] {
  return value.split(/(\s+)/).filter((token) => token.length > 0);
}

/** Agrupa tokens consecutivos da mesma operação para render em menos spans. */
function push(tokens: DiffToken[], op: DiffOp, value: string): void {
  const last = tokens.at(-1);
  if (last && last.op === op) {
    last.value += value;
    return;
  }
  tokens.push({ op, value });
}

function buildLcsTable(left: string[], right: string[]): number[][] {
  const table: number[][] = Array.from({ length: left.length + 1 }, () =>
    new Array<number>(right.length + 1).fill(0),
  );

  for (let i = left.length - 1; i >= 0; i -= 1) {
    for (let j = right.length - 1; j >= 0; j -= 1) {
      const row = table[i];
      const nextRow = table[i + 1];
      if (!row || !nextRow) continue;
      row[j] =
        left[i] === right[j]
          ? (nextRow[j + 1] ?? 0) + 1
          : Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0);
    }
  }
  return table;
}
